import React, { useState } from 'react';
import PageWrapper from '../components/PageWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Galaxy data for the cards - Image URLs set to your specified filenames!
const galaxyTypesData = [
  {
    id: 'spiral-galaxies',
    name: 'Spiral Galaxies',
    shortDescription: 'Characterized by a flat, rotating disk with spiral arms, like our Milky Way.',
    longDescription: 'Spiral galaxies are perhaps the most iconic type, known for their beautiful, swirling arms that extend from a central bulge. These arms are regions of active star formation, appearing brighter due to the presence of young, hot, blue stars. They also contain a significant amount of interstellar gas and dust. Our own Milky Way galaxy and the Andromeda galaxy are prime examples of spiral galaxies. They vary in the tightness and prominence of their spiral arms, often classified as barred spirals if they have a central bar-shaped structure.',
    imageUrl: '13.png', // Placeholder image, replace with actual path if available
    color: 'bg-indigo-600',
    borderColor: 'border-indigo-300/50'
  },
  {
    id: 'elliptical-galaxies',
    name: 'Elliptical Galaxies',
    shortDescription: 'Smooth, featureless, and egg-shaped, containing mostly older stars.',
    longDescription: 'Elliptical galaxies are the most abundant type of galaxy in the universe, ranging from nearly spherical to highly elongated shapes. Unlike spirals, they lack distinct structures like spiral arms or a central disk. They are generally composed of older, redder stars and contain very little interstellar gas and dust, meaning star formation is largely absent. They are thought to form through the mergers of smaller galaxies and can be found in a wide range of sizes, from dwarf ellipticals to colossal giant ellipticals at the centers of galaxy clusters.',
    imageUrl: '14.png', // Placeholder image, replace with actual path if available
    color: 'bg-purple-700',
    borderColor: 'border-purple-400/50'
  },
  {
    id: 'irregular-galaxies',
    name: 'Irregular Galaxies',
    shortDescription: 'Lack a distinct regular shape, often a result of gravitational interactions.',
    longDescription: 'Irregular galaxies do not fit into the spiral or elliptical classifications, exhibiting no clear structure or symmetry. Their chaotic appearance is often attributed to gravitational interactions with other galaxies, including collisions and near-misses, which can distort their shape and trigger bursts of star formation. Many irregular galaxies are rich in gas and dust, supporting active star-forming regions. The Magellanic Clouds, satellite galaxies of the Milky Way, are well-known examples of irregular galaxies.',
    imageUrl: '15.png', // Placeholder image, replace with actual path if available
    color: 'bg-green-700',
    borderColor: 'border-green-300/50'
  },
  {
    id: 'lenticular-galaxies',
    name: 'Lenticular Galaxies',
    shortDescription: 'Disk-shaped, but without prominent spiral arms, a bridge between spirals and ellipticals.',
    longDescription: 'Lenticular galaxies are a transitional type, sharing characteristics of both spiral and elliptical galaxies. They possess a prominent central bulge and a disk, much like a spiral, but lack the distinctive spiral arms and exhibit little ongoing star formation, similar to ellipticals. Their disks are typically smooth and featureless, primarily composed of older stars. It is believed that many lenticular galaxies may have once been spirals that have lost their interstellar gas and dust, halting star formation, possibly through interactions in dense galaxy environments.',
    imageUrl: '16.png', // Placeholder image, replace with actual path if available
    color: 'bg-teal-600',
    borderColor: 'border-teal-300/50'
  },
  {
    id: 'galaxy-clusters',
    name: 'Galaxy Clusters',
    shortDescription: 'Massive structures containing hundreds to thousands of galaxies, bound by gravity.',
    longDescription: 'Galaxy clusters are the largest gravitationally bound structures in the universe, consisting of hundreds to thousands of galaxies, vast amounts of hot gas (intracluster medium), and dark matter. The gravitational pull within these clusters is immense, influencing the evolution of individual galaxies, often leading to mergers and changes in their morphology. Our Local Group, which includes the Milky Way and Andromeda, is part of the larger Virgo Supercluster. Studying galaxy clusters helps astronomers understand the large-scale structure and evolution of the cosmos.',
    imageUrl: '17.png', // Placeholder image, replace with actual path if available
    color: 'bg-orange-700',
    borderColor: 'border-orange-300/50'
  },
];

// Sub-component for a single galaxy card
function GalaxyCard({ galaxy, isExpanded, onToggleExpand }) {
  return (
    <motion.div
      layout
      className={`relative p-6 border rounded-lg shadow-xl ${galaxy.borderColor} ${isExpanded ? 'col-span-full' : ''}`}
      transition={{ layout: { duration: 0.5, type: "spring", damping: 20, stiffness: 100 } }}
      style={{ overflow: 'hidden' }}
    >
      {/* Background Image Container */}
      <div
        className="absolute inset-0 transition-opacity duration-300 bg-center bg-cover rounded-lg opacity-60 group-hover:opacity-50"
        style={{ backgroundImage: `url(${galaxy.imageUrl})` }}
      ></div>

      {/* Content Overlay - ensures text is readable over image */}
      <div className="relative z-10 flex flex-col h-full">
        <motion.h3
          layout="position"
          className="mb-3 text-2xl font-bold text-white-300"
        >
          {galaxy.name}
        </motion.h3>
        <AnimatePresence mode="wait">
          {!isExpanded && (
            <motion.p
              key="short-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex-grow text-white-400"
            >
              {galaxy.shortDescription}
            </motion.p>
          )}
          {isExpanded && (
            <motion.p
              key="long-desc"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex-grow mt-4 text-white-400"
            >
              {galaxy.longDescription}
            </motion.p>
          )}
        </AnimatePresence>
        <motion.button
          className="self-start px-4 py-2 mt-6 font-semibold text-white transition-colors duration-300 bg-indigo-500 rounded-full hover:bg-indigo-600"
          onClick={() => onToggleExpand(galaxy.id)}
          layout="position"
        >
          {isExpanded ? 'View Less' : 'View More'}
        </motion.button>
      </div>
    </motion.div>
  );
}

export default function GalaxyPage() {
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleToggleExpand = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  return (
    <PageWrapper>
      <motion.div
        className="flex flex-col items-center justify-center min-h-screen p-8 text-white bg-gradient-to-br from-black to-indigo-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.h1
          className="mb-6 text-6xl font-extrabold text-cyan-400 drop-shadow-lg"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 120, damping: 10 }}
        >
          The Grand Galaxies
        </motion.h1>

        <motion.p
          className="max-w-3xl mb-12 text-xl text-center text-white-300"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 100, damping: 10 }}
        >
          Galaxies are vast systems of stars, stellar remnants, interstellar gas, dust, and dark matter, all bound together by gravity. Our own Milky Way is just one of billions of galaxies in the observable universe, each with its unique beauty and structure. Click "View More" to learn about different types of galaxies.
        </motion.p>

        <motion.div
          layout
          className="grid w-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          {galaxyTypesData.map((galaxy) => (
            <GalaxyCard
              key={galaxy.id}
              galaxy={galaxy}
              isExpanded={expandedCardId === galaxy.id}
              onToggleExpand={handleToggleExpand}
            />
          ))}
        </motion.div>

        <Link to="/explore" className="px-8 py-3 mt-16 text-xl font-semibold text-white no-underline transition-all duration-300 ease-in-out bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 hover:shadow-blue-500/50">
          Back to Explore
        </Link>
      </motion.div>
    </PageWrapper>
  );
}